import { memo, useEffect, useState, useContext } from "react";
import { Link } from "react-router-dom";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ptBR } from "date-fns/locale";
import "./OrcamentosPorAprovar.scss";
import { localeText } from "../../../../../shared/util/datatableTranslations";

import { DataGrid } from "@mui/x-data-grid";
import { generateExcelFile } from "../../../../../shared/components/excelUtils/exportExcel";

import { ClinicaContext } from "../../../../../shared/context/clinica-context";
import { AuthContext } from "../../../../../shared/context/auth-context";
import { useHttpClient } from "../../../../../shared/hooks/http-hook";
import EnviarEmailModal from "../../../../../shared/components/enviarEmailModal/EnviarEmailModal";

const PAGESIZE = 15;
function OrcamentosPorAprovar({ open, data, handleCloseModal }) {
  const [shownData, setShownData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [dataInicio, setDataInicio] = useState(null);
  const [dataFim, setDataFim] = useState(null);

  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [clientesSelecionados, setClientesSelecionados] = useState([]);
  const [orcamentosSelecionados, setOrcamentosSelecionados] = useState([]);

  const clinica = useContext(ClinicaContext);
  const auth = useContext(AuthContext);
  const { sendRequest } = useHttpClient();

  const handleSelectionChange = (ids) => {
    const seleccionados = shownData
      .filter((row) => ids.includes(row.id))
      .map((row) => ({
        id: row.idCliente,
        nome: row.nome,
        email: row.email ?? "",
        com_email: row.com_email ?? false,
        acompanhamentos: row.acompanhamentos,
      }));
    setClientesSelecionados(seleccionados);

    // Guardar ids dos orçamentos seleccionados para acompanhamento
    const orcamentos = shownData
      .filter((row) => ids.includes(row.id))
      .map((row) => ({ orcamentoId: row.id, tipo: "lembrete_orcamento" }));
    setOrcamentosSelecionados(orcamentos);
  };
  useEffect(() => {
    setShownData(data);
  }, [data]);

  useEffect(() => {
    filterData();
  }, [searchTerm, dataInicio, dataFim, data]);

  const columns = [
    {
      field: "nome",
      headerName: "Cliente",
      flex: 5,
      renderCell: (params) => {
        return (
          <Link
            to={`/clientes/${params.row.idCliente}-orcamento`}
            className="listaDivida__clientAnchor"
          >
            {params.row.nome}
          </Link>
        );
      },
    },
    {
      field: "contacto",
      headerName: "Contacto",
      flex: 1.5,
    },
    {
      field: "beneficio",
      headerName: "Tipo",
      flex: 1.5,
      renderCell: (params) => {
        return (
          <span>
            {params.row.beneficio} {params.row.orcamentoMisto ? "(Misto)" : ""}
          </span>
        );
      },
    },
    {
      field: "data",
      headerName: "Data",
      flex: 2,
    },
    {
      field: "total",
      headerName: "Total",
      flex: 1,
    },
    {
      field: "medico",
      headerName: "Médico(a)",
      flex: 2,
    },
    {
      field: "acompanhamentos",
      headerName: "Contactado",
      flex: 1,
      renderCell: (params) => {
        const total = params.row.acompanhamentos?.length || 0;
        return total > 0 ? (
          <span
            title={`${total} email(s) enviado(s)`}
            style={{ color: "#4caf50", fontWeight: 600, cursor: "default" }}
          >
            ✅ {total}x
          </span>
        ) : (
          <span style={{ color: "#ccc" }}>—</span>
        );
      },
    },
  ];

  const searchHandler = (e) => {
    setSearchTerm(e.target.value);
  };

  const filterData = () => {
    if (!data) return;

    let filteredData = data;

    // Filtro por nome
    if (searchTerm) {
      filteredData = filteredData.filter((dt) =>
        dt.nome.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    // Filtro por data
    if (dataInicio || dataFim) {
      filteredData = filteredData.filter((dt) => {
        if (!dt.data) return false;

        // Parse da data no formato "11-04-2024 09:53"
        const [datePart] = dt.data.split(" "); // Pega só a parte da data
        const [day, month, year] = datePart.split("-");
        const dataOrcamento = new Date(year, month - 1, day); // month - 1 porque Date usa mês base 0

        if (dataInicio && dataFim) {
          const inicio = new Date(dataInicio);
          const fim = new Date(dataFim);
          inicio.setHours(0, 0, 0, 0);
          fim.setHours(23, 59, 59, 999);
          return dataOrcamento >= inicio && dataOrcamento <= fim;
        } else if (dataInicio) {
          const inicio = new Date(dataInicio);
          inicio.setHours(0, 0, 0, 0);
          return dataOrcamento >= inicio;
        } else if (dataFim) {
          const fim = new Date(dataFim);
          fim.setHours(23, 59, 59, 999);
          return dataOrcamento <= fim;
        }

        return true;
      });
    }

    setShownData(filteredData);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setDataInicio(null);
    setDataFim(null);
  };

  const handleExportar = () => {
    const sheetData = shownData.map((dt) => ({
      Cliente: dt.nome,
      Contacto: dt.contacto || "-",
      Tipo_Orcamento: dt.beneficio,
      Data: dt.data,
      Total: dt.total,
      medico: dt.medico,
    }));

    const data = new Date();
    const dia = String(data.getDate()).padStart(2, "0");
    const mes = String(data.getMonth() + 1).padStart(2, "0"); // mês começa em 0
    const ano = data.getFullYear();

    const dataEmString = `${dia}-${mes}-${ano}`;

    generateExcelFile(
      `Orcamentos por Aprovar ${dataEmString}.xlsx`,
      "Orcamentos",
      sheetData,
    );
  };

  return (
    <Modal
      open={open}
      onClose={handleCloseModal.bind(null, "orcamentos")}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box
        className="fluxo-atendimento__modal__box"
        id="orcamentos-por-aprovar__modal"
      >
        <div className="orcamentos-por-aprovar__modal__cabecalho">
          <h1 className="fluxo-atendimento__modal__titulo">Orçamentos</h1>

          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={ptBR}
          >
            <div className="orcamentos-por-aprovar__filtros">
              <div className="date-filters__container">
                <div className="date-filter">
                  <DatePicker
                    label="Data Início"
                    value={dataInicio}
                    onChange={(newValue) => setDataInicio(newValue)}
                    renderInput={(params) => (
                      <TextField {...params} size="small" variant="outlined" />
                    )}
                    inputFormat="dd/MM/yyyy"
                  />
                </div>

                <div className="date-filter">
                  <DatePicker
                    label="Data Fim"
                    value={dataFim}
                    onChange={(newValue) => setDataFim(newValue)}
                    renderInput={(params) => (
                      <TextField {...params} size="small" variant="outlined" />
                    )}
                    inputFormat="dd/MM/yyyy"
                  />
                </div>

                <div className="search__datatable__container">
                  <span>Procurar</span>
                  <input
                    type="search"
                    className="search__datatable__input"
                    placeholder="Procurar"
                    value={searchTerm}
                    onChange={searchHandler}
                  />
                </div>

                <button
                  className="clear-filters-btn"
                  onClick={handleExportar}
                  title="Exportar Xls"
                >
                  Exportar
                </button>

                <button
                  className="clear-filters-btn"
                  onClick={clearFilters}
                  title="Limpar filtros"
                >
                  Limpar
                </button>

                <button
                  className="clear-filters-btn"
                  onClick={() => setEmailModalOpen(true)}
                  disabled={clientesSelecionados.length === 0}
                  title="Enviar email aos seleccionados"
                >
                  Enviar Email ({clientesSelecionados.length})
                </button>
              </div>
            </div>
          </LocalizationProvider>
        </div>

        <div className="orcamentos-por-aprovar__modal__container">
          <DataGrid
            autoWidth
            className="datagrid"
            rows={shownData}
            columns={columns}
            pageSize={PAGESIZE}
            rowsPerPage={PAGESIZE}
            localeText={localeText}
            checkboxSelection
            onSelectionModelChange={handleSelectionChange}
          />
        </div>

        <div
          style={{
            gridColumn: "1/-1",
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "25px",
          }}
        >
          <span
            className="cancel-btn"
            onClick={handleCloseModal.bind(null, "orcamentos")}
          >
            Fechar
          </span>
        </div>

        <EnviarEmailModal
          open={emailModalOpen}
          onClose={() => setEmailModalOpen(false)}
          clientes={clientesSelecionados}
          clinica={clinica}
          sendRequest={sendRequest}
          auth={auth}
          acompanhamentoIds={orcamentosSelecionados}
          onEmailEnviado={() => {
            setShownData((prev) =>
              prev.map((row) => {
                const encontrado = orcamentosSelecionados.find(
                  (a) => a.orcamentoId === row.id,
                );
                if (encontrado) {
                  return {
                    ...row,
                    acompanhamentos: [
                      ...(row.acompanhamentos || []),
                      { tipo: encontrado.tipo, dataEnvio: new Date() },
                    ],
                  };
                }
                return row;
              }),
            );
          }}
          tipo="orcamentoPorAprovar"
          endpoint={`${process.env.REACT_APP_BACKEND_LINK}/api/email/orcamentosporaprovar/enviar`}
          extraBody={{ acompanhamentoIds: orcamentosSelecionados }}
        />
      </Box>
    </Modal>
  );
}

export default memo(OrcamentosPorAprovar);
